import { TranslateService } from '@ngx-translate/core';
import { DateFilterTypeEnum } from 'src/app/enums/dateFilterType.enum';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { DateRange } from './custom-date-range-controller';

export interface DateRangeOption {
    value: number;
    label: string;
}

export interface DateRangeOptionWithRange {
    value: number;
    displayName: string;
}

export function buildDateRangeOptionsWithRange(
    options: DateRangeOption[],
    translate: TranslateService,
    dateFormatService: DateFormatService,
    now: Date = new Date(),
): DateRangeOptionWithRange[] {
    return options.map(option => {
        const baseName = translate.instant(option.label);
        if (option.value === DateFilterTypeEnum.Custom || option.value === DateFilterTypeEnum.AllDates) {
            return { value: option.value, displayName: baseName };
        }
        const range = resolveDateFilterRange(option.value, now);
        if (!range) return { value: option.value, displayName: baseName };
        const startLabel = dateFormatService.formatDateTimeToString(range.start, dateFormatService.monthDayYearFormat);
        const endLabel = dateFormatService.formatDateTimeToString(range.end, dateFormatService.monthDayYearFormat);
        return { value: option.value, displayName: `${baseName} (${startLabel} – ${endLabel})` };
    });
}

export function resolveDateFilterRange(filterType: number, now: Date = new Date()): DateRange | null {
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const currentQuarter = Math.floor(currentMonth / 3);
    switch (filterType) {
        case DateFilterTypeEnum.ThisYear:
            return { start: new Date(currentYear, 0, 1), end: now };
        case DateFilterTypeEnum.ThisQuarter:
            return { start: new Date(currentYear, currentQuarter * 3, 1), end: now };
        case DateFilterTypeEnum.ThisMonth:
            return { start: new Date(currentYear, currentMonth, 1), end: now };
        case DateFilterTypeEnum.LastYear: {
            const start = new Date(now);
            start.setFullYear(start.getFullYear() - 1);
            return { start, end: now };
        }
        case DateFilterTypeEnum.LastQuarter: {
            const lastQuarterStart = new Date(currentYear, (currentQuarter - 1) * 3, 1);
            const lastQuarterEnd = new Date(currentYear, currentQuarter * 3, 0, 23, 59, 59);
            return { start: lastQuarterStart, end: lastQuarterEnd };
        }
        case DateFilterTypeEnum.LastMonth: {
            const lastMonthStart = new Date(currentYear, currentMonth - 1, 1);
            const lastMonthEnd = new Date(currentYear, currentMonth, 0, 23, 59, 59);
            return { start: lastMonthStart, end: lastMonthEnd };
        }
        default:
            return null;
    }
}
